import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Wallet, TrendingUp, Zap } from "lucide-react";
import { SiMeta, SiGoogle, SiTiktok, SiSnapchat } from "react-icons/si";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { useLanguage } from "../../contexts/LanguageContext";

const COPY = {
    en: {
        chip: "Established agency infrastructure · 7 platforms",
        h1a: "Every ad account you run,",
        h1b: "in one powerful panel.",
        subtitle:
            "Agency ad accounts for Meta, Google, TikTok, Snapchat and more. Fund with crypto, go live in minutes — no monthly subscription, and fees fall as your volume grows.",
        ctaApply: "Get your accounts",
        ctaPricing: "See pricing",
        free: "Free to start · no card · no subscription",
        trust: (n) => `Trusted by ${n}+ media buyers`,
        rated: (r) => `Rated ${r}/5 on verified reviews`,
    },
    tr: {
        chip: "Köklü ajans altyapısı · 7 platform",
        h1a: "Tüm reklam hesaplarınız,",
        h1b: "tek güçlü panelde.",
        subtitle:
            "Meta, Google, TikTok, Snapchat ve daha fazlası için ajans reklam hesapları. Kripto ile yükle, dakikalar içinde yayında — aylık abonelik yok, komisyon hacimle düşer.",
        ctaApply: "Hesaplarını al",
        ctaPricing: "Fiyatları gör",
        free: "Ücretsiz başla · kart yok · abonelik yok",
        trust: (n) => `${n}+ medya satın almacı güveniyor`,
        rated: (r) => `Doğrulanmış yorumlarda ${r}/5`,
    },
    es: {
        chip: "Infraestructura de agencia consolidada · 7 plataformas",
        h1a: "Todas tus cuentas de anuncios,",
        h1b: "en un panel potente.",
        subtitle:
            "Cuentas de agencia para Meta, Google, TikTok, Snapchat y más. Recarga con cripto, activa en minutos — sin suscripción mensual y con comisiones que bajan con el volumen.",
        ctaApply: "Obtener cuentas",
        ctaPricing: "Ver precios",
        free: "Gratis para empezar · sin tarjeta · sin suscripción",
        trust: (n) => `Más de ${n} media buyers confían`,
        rated: (r) => `Calificación ${r}/5 en reseñas`,
    },
    pt: {
        chip: "Infraestrutura de agência consolidada · 7 plataformas",
        h1a: "Todas as suas contas de anúncios,",
        h1b: "num painel poderoso.",
        subtitle:
            "Contas de agência para Meta, Google, TikTok, Snapchat e mais. Abasteça com cripto, ative em minutos — sem assinatura mensal e com taxas que caem com o volume.",
        ctaApply: "Obter contas",
        ctaPricing: "Ver preços",
        free: "Grátis para começar · sem cartão · sem assinatura",
        trust: (n) => `Mais de ${n} media buyers confiam`,
        rated: (r) => `Avaliação ${r}/5 em reviews`,
    },
    ru: {
        chip: "Надёжная агентская инфраструктура · 7 платформ",
        h1a: "Все ваши рекламные аккаунты",
        h1b: "в одной мощной панели.",
        subtitle:
            "Агентские аккаунты для Meta, Google, TikTok, Snapchat и других. Пополняйте криптой, запуск за минуты — без подписки, комиссия падает с объёмом.",
        ctaApply: "Получить аккаунты",
        ctaPricing: "Смотреть тарифы",
        free: "Бесплатный старт · без карты · без подписки",
        trust: (n) => `Нам доверяют ${n}+ медиабайеров`,
        rated: (r) => `Оценка ${r}/5 по отзывам`,
    },
};

const PANEL_LABELS = {
    en: { balance: "Wallet balance", spend: "Spend this month", fee: "Current fee tier", accounts: "Active accounts", active: "Active", byPlatform: "by platform", caption: "Your Adrise panel — balances, spend and account status in real time." },
    tr: { balance: "Cüzdan bakiyesi", spend: "Bu ay harcama", fee: "Güncel komisyon", accounts: "Aktif hesaplar", active: "Aktif", byPlatform: "platforma göre", caption: "Adrise paneliniz — bakiye, harcama ve hesap durumu gerçek zamanlı." },
    es: { balance: "Saldo", spend: "Gasto este mes", fee: "Comisión actual", accounts: "Cuentas activas", active: "Activa", byPlatform: "por plataforma", caption: "Tu panel Adrise — saldo, gasto y estado en tiempo real." },
    pt: { balance: "Saldo", spend: "Gasto este mês", fee: "Taxa atual", accounts: "Contas ativas", active: "Ativa", byPlatform: "por plataforma", caption: "Seu painel Adrise — saldo, gasto e status em tempo real." },
    ru: { balance: "Баланс", spend: "Расход за месяц", fee: "Текущая комиссия", accounts: "Активные аккаунты", active: "Активен", byPlatform: "по платформам", caption: "Ваша панель Adrise — баланс, расход и статус в реальном времени." },
};

const bars = [
    { Icon: SiMeta, label: "Meta", pct: 82, color: "#EAE6DC" },
    { Icon: SiGoogle, label: "Google", pct: 64, color: "#BFB9AC" },
    { Icon: SiTiktok, label: "TikTok", pct: 48, color: "#2FA57C" },
    { Icon: SiSnapchat, label: "Snap", pct: 33, color: "#807A70" },
];

export default function HeroSection() {
    const { settings } = useSiteSettings();
    const { language } = useLanguage();
    const c = COPY[language] || COPY.en;
    const p = PANEL_LABELS[language] || PANEL_LABELS.en;

    return (
        <section data-testid="hero-section" className="relative pt-40 md:pt-48 pb-16 md:pb-24 overflow-hidden ring-launch">
            <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[820px] h-[820px] rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0, 0, 0,0.16) 0%, rgba(10,8,20,0) 68%)" }} />

            <div className="container-skala relative">
                <div className="max-w-3xl mx-auto text-center space-y-6 reveal-up">
                    <div className="inline-flex items-center gap-2 rounded-full border border-highlight/25 bg-highlight/[0.06] px-4 py-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-highlight animate-pulse" />
                        <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-highlight">{c.chip}</span>
                    </div>

                    <h1 data-testid="hero-headrise" className="font-display font-semibold text-4xl sm:text-6xl md:text-[5rem] leading-[1.02] tracking-tight">
                        {c.h1a}<br />
                        <span className="serif-accent text-foreground/90">{c.h1b}</span>
                    </h1>

                    <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">{c.subtitle}</p>

                    <div className="flex flex-wrap gap-3 justify-center pt-1">
                        <Link to="/signup" data-testid="hero-cta-apply-link">
                            <Button data-testid="hero-cta-apply" size="lg" className="btn-highlight rounded-xl h-12 px-7 glow-highlight">
                                {c.ctaApply}<ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                        <a href="#pricing" data-testid="hero-cta-pricing">
                            <Button size="lg" variant="outline" className="border-white/15 bg-white/[0.02] hover:bg-white/[0.06] h-12 px-7 rounded-xl">{c.ctaPricing}</Button>
                        </a>
                    </div>
                    <p className="text-xs font-mono text-highlight inline-flex items-center gap-1.5" data-testid="hero-cta-free-hint">
                        <span className="w-1.5 h-1.5 rounded-full bg-highlight" /> {c.free}
                    </p>
                </div>

                {/* Product dashboard mock */}
                <div className="max-w-5xl mx-auto mt-14 reveal-up">
                    <div className="rounded-3xl border border-white/10 bg-card p-3 md:p-4 shadow-[0_50px_120px_-40px_rgba(0, 0, 0,0.5)]">
                        <div className="rounded-2xl bg-background/60 border border-white/5 p-5 md:p-7">
                            <div className="grid md:grid-cols-3 gap-4">
                                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                                    <div className="flex items-center gap-2 text-muted-foreground text-xs"><Wallet className="w-4 h-4 text-highlight" />{p.balance}</div>
                                    <div className="mt-2 font-display font-bold text-3xl">$48,250</div>
                                    <div className="mt-1 text-[11px] font-mono text-highlight">USDT · USDC</div>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                                    <div className="flex items-center gap-2 text-muted-foreground text-xs"><TrendingUp className="w-4 h-4 text-primary" />{p.spend}</div>
                                    <div className="mt-2 font-display font-bold text-3xl">$312,900</div>
                                    <div className="mt-1 text-[11px] font-mono text-primary">+18.4% ↑</div>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                                    <div className="flex items-center gap-2 text-muted-foreground text-xs"><Zap className="w-4 h-4 text-highlight" />{p.fee}</div>
                                    <div className="mt-2 font-display font-bold text-3xl">2.0%</div>
                                    <div className="mt-1 text-[11px] font-mono text-muted-foreground">Scale tier</div>
                                </div>
                            </div>

                            <div className="mt-5 grid md:grid-cols-5 gap-5 items-end">
                                <div className="md:col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                                    <div className="text-xs text-muted-foreground mb-4">{p.spend} · {p.byPlatform}</div>
                                    <div className="flex items-end gap-4 h-32">
                                        {bars.map((b) => (
                                            <div key={b.label} className="flex-1 h-full flex flex-col items-center justify-end gap-2">
                                                <div className="w-full rounded-t-lg" style={{ height: `${b.pct}%`, minHeight: "8px", background: `linear-gradient(180deg, ${b.color}, rgba(0, 0, 0,0.15))` }} />
                                                <b.Icon className="w-4 h-4" style={{ color: b.color }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="md:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
                                    <div className="flex items-center justify-between text-xs text-muted-foreground">{p.accounts}<span className="font-mono text-highlight">04</span></div>
                                    {bars.map((b) => (
                                        <div key={b.label} className="flex items-center justify-between">
                                            <span className="inline-flex items-center gap-2 text-sm"><b.Icon className="w-4 h-4" style={{ color: b.color }} />{b.label}</span>
                                            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-highlight"><span className="w-1.5 h-1.5 rounded-full bg-highlight" />{p.active}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-center text-xs font-mono text-muted-foreground mt-4">{p.caption}</p>
                </div>

                <div className="flex items-center justify-center gap-5 pt-10">
                    <div className="flex -space-x-2">
                        {[
                            "https://images.unsplash.com/photo-1758600587730-a11917c13b85?crop=entropy&cs=srgb&fm=jpg&w=80&q=85",
                            "https://images.unsplash.com/photo-1767175620484-1ed37931a0d1?crop=entropy&cs=srgb&fm=jpg&w=80&q=85",
                            "https://images.unsplash.com/photo-1764546373114-2d7a87221733?crop=entropy&cs=srgb&fm=jpg&w=80&q=85",
                            "https://images.pexels.com/photos/28513054/pexels-photo-28513054.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=80&w=80",
                        ].map((src) => (
                            <img key={src} src={src} alt="" className="w-9 h-9 rounded-full border-2 border-background object-cover" />
                        ))}
                    </div>
                    <div className="text-left">
                        <p className="text-xs font-mono tracking-wider text-muted-foreground">{c.trust(settings.trustpilot_count)}</p>
                        <p className="text-xs font-mono tracking-wider text-foreground/80 mt-0.5">★ ★ ★ ★ ★ &nbsp;<span className="text-muted-foreground">{c.rated(settings.trustpilot_rating)}</span></p>
                    </div>
                </div>
            </div>
        </section>
    );
}
